import { sha256 } from "../domain/content_hash";
import { formatStoredPath, normalizeRelativePath } from "../domain/paths";
import {
  type OperationJournal,
  type OperationPhase,
  type OperationType,
  parseOperationJournal,
  serializeOperationJournal,
  type WorkspaceOperation,
} from "./operation_journal";
import { serializeTrashMetadata, TRASH_METADATA_FILE, type TrashMetadata } from "./trash_metadata";
import type { WorkspaceGateway } from "./workspace_gateway";

export const SHOTBOARD_SYSTEM_PATH = [".shotboard"] as const;
export const OPERATION_JOURNAL_PATH = [".shotboard", "operations.json"] as const;
export const TRASH_PATH = [".shotboard", "trash"] as const;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

interface TreeEntry {
  readonly kind: "file" | "directory";
  readonly relativePath: readonly string[];
}

export interface DirectoryOperationRequest {
  readonly type: OperationType;
  readonly sourcePath: readonly string[];
  readonly destinationPath: readonly string[];
}

export interface DirectoryOperationProgress {
  readonly phase: OperationPhase;
  readonly completedEntries: number;
  readonly totalEntries: number;
  readonly currentPath: readonly string[] | null;
}

export interface PendingDirectoryOperation {
  readonly operation: WorkspaceOperation;
  readonly canRollback: boolean;
}

export interface DirectoryOperationOptions {
  readonly now?: () => Date;
  readonly createId?: () => string;
  readonly onProgress?: (progress: DirectoryOperationProgress) => void;
}

/** Describes an unreadable journal without mutating uncertain workspace state. */
export class OperationJournalError extends Error {
  public readonly kind: "invalid" | "newer";
  public readonly raw: string;

  /** Retains the journal category and original bytes for later recovery UI. */
  public constructor(kind: OperationJournalError["kind"], message: string, raw: string) {
    super(message);
    this.name = "OperationJournalError";
    this.kind = kind;
    this.raw = raw;
  }
}

/** Executes copy-verify-delete directory operations through one durable journal. */
export class DirectoryOperationService {
  private readonly gateway: WorkspaceGateway;
  private readonly now: () => Date;
  private readonly createId: () => string;
  private readonly onProgress: (progress: DirectoryOperationProgress) => void;

  /** Retains the workspace gateway and deterministic operation dependencies. */
  public constructor(gateway: WorkspaceGateway, options: DirectoryOperationOptions = {}) {
    this.gateway = gateway;
    this.now = options.now ?? (() => new Date());
    this.createId = options.createId ?? (() => crypto.randomUUID());
    this.onProgress = options.onProgress ?? (() => undefined);
  }

  /** Records a collision-free operation before performing any copy side effect. */
  public async start(request: DirectoryOperationRequest, signal?: AbortSignal): Promise<void> {
    const sourcePath = normalizeRelativePath(request.sourcePath);
    const destinationPath = normalizeRelativePath(request.destinationPath);
    this.validatePaths(request.type, sourcePath, destinationPath);
    await this.ensureSystemDirectories();

    if ((await this.inspect()) !== null) {
      throw new Error("Finish or roll back the pending workspace operation first.");
    }
    if (!(await this.gateway.fileExists(sourcePath))) {
      throw new Error("The operation source no longer exists.");
    }
    if (await this.gateway.fileExists(destinationPath)) {
      throw new Error("The operation destination already exists.");
    }
    const destinationParent = destinationPath.slice(0, -1);
    if (!(await this.gateway.fileExists(destinationParent))) {
      throw new Error("The operation destination folder no longer exists.");
    }

    const operation: WorkspaceOperation = {
      id: this.createId(),
      type: request.type,
      phase: "planned",
      sourcePath: [...sourcePath],
      destinationPath: [...destinationPath],
      createdAt: this.now().toISOString(),
    };
    await this.writeOperation(operation);
    await this.resume(signal);
  }

  /** Returns the current recoverable operation without modifying disk state. */
  public async inspect(): Promise<PendingDirectoryOperation | null> {
    if (!(await this.gateway.fileExists(OPERATION_JOURNAL_PATH))) {
      return null;
    }
    const raw = textDecoder.decode(await this.gateway.readFile(OPERATION_JOURNAL_PATH));
    const parsed = parseOperationJournal(raw);
    if (parsed.kind === "newer") {
      throw new OperationJournalError(
        "newer",
        `This operation journal uses newer schema ${parsed.schemaVersion}.`,
        raw,
      );
    }
    if (parsed.kind === "invalid") {
      throw new OperationJournalError("invalid", parsed.reason, raw);
    }
    const operation = parsed.journal.operations[0];
    if (operation === undefined) {
      throw new OperationJournalError("invalid", "The operation journal is empty.", raw);
    }
    const sourceExists = await this.gateway.fileExists(operation.sourcePath);
    return {
      operation,
      canRollback:
        sourceExists &&
        (operation.phase === "planned" ||
          operation.phase === "copying" ||
          operation.phase === "verifying"),
    };
  }

  /** Resumes the pending operation from its last durable phase until completion. */
  public async resume(signal?: AbortSignal): Promise<void> {
    let pending = await this.inspect();
    if (pending === null) {
      return;
    }

    while (pending !== null) {
      assertNotAborted(signal);
      const operation = pending.operation;
      switch (operation.phase) {
        case "planned":
          await this.writeOperation({ ...operation, phase: "copying" });
          break;
        case "copying":
          await this.copyOperation(operation, signal);
          if (operation.type === "trash") {
            await this.writeTrashMetadata(operation);
          }
          await this.writeOperation({ ...operation, phase: "verifying" });
          break;
        case "verifying":
          await this.verifyOperation(operation, signal);
          await this.writeOperation({ ...operation, phase: "destination_complete" });
          break;
        case "destination_complete":
          await this.verifyOperation(operation, signal);
          if (operation.type === "duplicate") {
            await this.clearJournal();
            return;
          }
          await this.writeOperation({ ...operation, phase: "deleting_source" });
          break;
        case "deleting_source":
          if (!(await this.gateway.fileExists(operation.sourcePath))) {
            await this.clearJournal();
            return;
          }
          await this.verifyOperation(operation, signal);
          await this.gateway.removeEntry(operation.sourcePath, { recursive: true });
          await this.clearJournal();
          return;
      }
      pending = await this.inspect();
    }
  }

  /** Removes only an incomplete operation-owned destination while the source remains intact. */
  public async rollback(): Promise<void> {
    const pending = await this.inspect();
    if (pending === null) {
      return;
    }
    if (!pending.canRollback) {
      throw new Error("This verified operation must be resumed; rollback is no longer safe.");
    }
    if (await this.gateway.fileExists(pending.operation.destinationPath)) {
      await this.gateway.removeEntry(pending.operation.destinationPath, { recursive: true });
    }
    await this.clearJournal();
  }

  /** Permanently empties only the reserved trash after confirming no operation is pending. */
  public async emptyTrash(): Promise<void> {
    if ((await this.inspect()) !== null) {
      throw new Error("Finish or roll back the pending workspace operation first.");
    }
    if (await this.gateway.fileExists(TRASH_PATH)) {
      await this.gateway.removeEntry(TRASH_PATH, { recursive: true });
    }
    await this.gateway.createDirectory(TRASH_PATH);
  }

  /** Copies all source entries idempotently into the operation-owned destination. */
  private async copyOperation(operation: WorkspaceOperation, signal?: AbortSignal): Promise<void> {
    const entries = (await collectTree(this.gateway, operation.sourcePath)).filter(
      (entry) => !shouldSkipTrashMetadata(operation, entry.relativePath),
    );
    if (!(await this.gateway.fileExists(operation.destinationPath))) {
      await this.gateway.createDirectory(operation.destinationPath);
    }
    this.emitProgress(operation.phase, 0, entries.length, null);

    let completedEntries = 0;
    for (const entry of entries) {
      assertNotAborted(signal);
      const source = [...operation.sourcePath, ...entry.relativePath];
      const destination = [...operation.destinationPath, ...entry.relativePath];
      if (entry.kind === "directory") {
        if (!(await this.gateway.fileExists(destination))) {
          await this.gateway.createDirectory(destination);
        }
      } else {
        await this.gateway.writeFileSafely(destination, await this.gateway.readFile(source));
      }
      completedEntries += 1;
      this.emitProgress(operation.phase, completedEntries, entries.length, entry.relativePath);
    }
  }

  /** Compares the complete relative entry set, byte length, and SHA-256 file identities. */
  private async verifyOperation(
    operation: WorkspaceOperation,
    signal?: AbortSignal,
  ): Promise<void> {
    if (!(await this.gateway.fileExists(operation.destinationPath))) {
      throw new Error("The operation destination is missing before verification.");
    }
    const sourceEntries = (await collectTree(this.gateway, operation.sourcePath)).filter(
      (entry) => !shouldSkipTrashMetadata(operation, entry.relativePath),
    );
    const destinationEntries = (await collectTree(this.gateway, operation.destinationPath)).filter(
      (entry) => !(operation.type === "trash" && isTrashMetadata(entry.relativePath)),
    );
    const sourceMap = new Map(sourceEntries.map((entry) => [entryKey(entry), entry]));
    const destinationMap = new Map(destinationEntries.map((entry) => [entryKey(entry), entry]));
    if (
      sourceMap.size !== destinationMap.size ||
      [...sourceMap.keys()].some((key) => !destinationMap.has(key))
    ) {
      throw new Error("The copied directory entry set failed verification.");
    }

    const files = sourceEntries.filter((entry) => entry.kind === "file");
    this.emitProgress("verifying", 0, files.length, null);
    let completedEntries = 0;
    for (const sourceEntry of files) {
      assertNotAborted(signal);
      const sourceBytes = await this.gateway.readFile([
        ...operation.sourcePath,
        ...sourceEntry.relativePath,
      ]);
      const destinationBytes = await this.gateway.readFile([
        ...operation.destinationPath,
        ...sourceEntry.relativePath,
      ]);
      if (
        sourceBytes.byteLength !== destinationBytes.byteLength ||
        (await sha256(sourceBytes)) !== (await sha256(destinationBytes))
      ) {
        throw new Error(
          `The copied file failed verification: ${formatStoredPath(sourceEntry.relativePath)}.`,
        );
      }
      completedEntries += 1;
      this.emitProgress("verifying", completedEntries, files.length, sourceEntry.relativePath);
    }
  }

  /** Writes the original location into a copied trash entry before source deletion. */
  private async writeTrashMetadata(operation: WorkspaceOperation): Promise<void> {
    const metadata: TrashMetadata = {
      format: "shotboard-trash-entry",
      schemaVersion: 1,
      originalPath: operation.sourcePath,
      trashedAt: operation.createdAt,
    };
    await this.gateway.writeFileSafely(
      [...operation.destinationPath, TRASH_METADATA_FILE],
      textEncoder.encode(serializeTrashMetadata(metadata)),
    );
  }

  /** Validates reserved locations and prevents copying a directory into itself. */
  private validatePaths(
    type: OperationType,
    sourcePath: readonly string[],
    destinationPath: readonly string[],
  ): void {
    if (
      pathsEqual(sourcePath, destinationPath) ||
      isPathPrefix(sourcePath, destinationPath) ||
      isPathPrefix(destinationPath, sourcePath)
    ) {
      throw new Error("Operation source and destination must be independent directories.");
    }
    const sourceInSystem = sourcePath[0] === ".shotboard";
    const destinationInSystem = destinationPath[0] === ".shotboard";
    if (type === "trash") {
      if (sourceInSystem || !isPathPrefix(TRASH_PATH, destinationPath)) {
        throw new Error("Trash operations must move a workspace board into reserved trash.");
      }
      return;
    }
    if (type === "restore") {
      if (!isPathPrefix(TRASH_PATH, sourcePath) || destinationInSystem) {
        throw new Error("Restore operations must move a trash board back into the workspace.");
      }
      return;
    }
    if (sourceInSystem || destinationInSystem) {
      throw new Error("Reserved .shotboard paths cannot be managed as workspace content.");
    }
  }

  /** Creates only Shotboard's reserved support directories when absent. */
  private async ensureSystemDirectories(): Promise<void> {
    if (!(await this.gateway.fileExists(SHOTBOARD_SYSTEM_PATH))) {
      await this.gateway.createDirectory(SHOTBOARD_SYSTEM_PATH);
    }
    if (!(await this.gateway.fileExists(TRASH_PATH))) {
      await this.gateway.createDirectory(TRASH_PATH);
    }
  }

  /** Replaces the single-operation journal before the next operation phase. */
  private async writeOperation(operation: WorkspaceOperation): Promise<void> {
    const journal: OperationJournal = {
      format: "shotboard-operations",
      schemaVersion: 1,
      operations: [operation],
    };
    await this.gateway.writeFileSafely(
      OPERATION_JOURNAL_PATH,
      textEncoder.encode(serializeOperationJournal(journal)),
    );
  }

  /** Deletes only the completed operation journal. */
  private async clearJournal(): Promise<void> {
    if (await this.gateway.fileExists(OPERATION_JOURNAL_PATH)) {
      await this.gateway.removeEntry(OPERATION_JOURNAL_PATH, { recursive: false });
    }
  }

  /** Reports bounded copy or verification progress to the shell. */
  private emitProgress(
    phase: OperationPhase,
    completedEntries: number,
    totalEntries: number,
    currentPath: readonly string[] | null,
  ): void {
    this.onProgress({ phase, completedEntries, totalEntries, currentPath });
  }
}

/** Recursively collects a stable relative directory tree without reading file bytes. */
async function collectTree(
  gateway: WorkspaceGateway,
  rootPath: readonly string[],
): Promise<readonly TreeEntry[]> {
  const collected: TreeEntry[] = [];

  /** Visits child directories in gateway-sorted order. */
  async function visit(relativePath: readonly string[]): Promise<void> {
    const entries = await gateway.listDirectory([...rootPath, ...relativePath]);
    for (const entry of entries) {
      const childPath = [...relativePath, entry.name];
      collected.push({ kind: entry.kind, relativePath: childPath });
      if (entry.kind === "directory") {
        await visit(childPath);
      }
    }
  }

  await visit([]);
  return collected;
}

/** Creates a stable key that also distinguishes files from directories. */
function entryKey(entry: TreeEntry): string {
  return `${entry.kind}:${formatStoredPath(entry.relativePath)}`;
}

/** Omits trash-only metadata from restore copying and every content comparison. */
function shouldSkipTrashMetadata(
  operation: WorkspaceOperation,
  relativePath: readonly string[],
): boolean {
  return operation.type === "restore" && isTrashMetadata(relativePath);
}

/** Identifies the one reserved metadata file at a trash entry root. */
function isTrashMetadata(relativePath: readonly string[]): boolean {
  return relativePath.length === 1 && relativePath[0] === TRASH_METADATA_FILE;
}

/** Tests path ancestry using normalized segment identity. */
function isPathPrefix(prefix: readonly string[], candidate: readonly string[]): boolean {
  return (
    prefix.length < candidate.length &&
    prefix.every((segment, index) => candidate[index] === segment)
  );
}

/** Tests exact normalized path identity. */
function pathsEqual(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((segment, index) => right[index] === segment);
}

/** Converts cancellation into a stable recoverable interruption. */
function assertNotAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new DOMException("The workspace operation was cancelled.", "AbortError");
  }
}
